"use client"

import React, { useState, useEffect } from "react"
import { Button } from "../ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogPortal,
  DialogOverlay,
  DialogTitle,
} from "../ui/dialog"
import { Label } from "../ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select"
import { getSharedCookie, setSharedCookie } from "../lib/utils"

export const CURRENCY_STORAGE_KEY = "AskFinz_currency_pairs"

export type CurrencyPair = { base: string; symbol: string }

const COMMON_CURRENCIES = [
  "USD", "EUR", "GBP", "JPY", "CHF", "CAD", "AUD", "INR", "CNY", "BRL",
  "MXN", "KRW", "SGD", "HKD", "NOK", "SEK", "DKK", "NZD", "ZAR", "RUB",
]

const DEFAULT_PAIRS: CurrencyPair[] = [
  { base: "EUR", symbol: "USD" },
  { base: "EUR", symbol: "GBP" },
  { base: "EUR", symbol: "JPY" },
  { base: "EUR", symbol: "INR" },
]

function parseStored(raw: string | null): CurrencyPair[] {
  if (!raw) return DEFAULT_PAIRS
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed?.pairs) && parsed.pairs.length > 0) {
      return parsed.pairs
        .slice(0, 4)
        .filter((p: unknown) => p && typeof p === "object" && "base" in p && "symbol" in p)
        .map((p: { base: string; symbol: string }) => ({ base: p.base, symbol: p.symbol }))
    }
    if (parsed?.base && Array.isArray(parsed?.symbols)) {
      return parsed.symbols.slice(0, 4).map((s: string) => ({ base: parsed.base, symbol: s }))
    }
  } catch { }
  return DEFAULT_PAIRS
}

export function getStoredCurrencyPairs(): CurrencyPair[] {
  if (typeof window === "undefined") return DEFAULT_PAIRS
  return parseStored(getSharedCookie(CURRENCY_STORAGE_KEY))
}

export function setStoredCurrencyPairs(pairs: CurrencyPair[]) {
  if (typeof window === "undefined") return
  try {
    setSharedCookie(CURRENCY_STORAGE_KEY, JSON.stringify({
      pairs: (pairs || []).slice(0, 4).filter((p) => p.base && p.symbol),
    }))
  } catch { }
}

const MAX_PAIRS = 4

interface CurrencySelectDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave?: (pairs: CurrencyPair[]) => void
}

export function CurrencySelectDialog({
  open,
  onOpenChange,
  onSave,
}: CurrencySelectDialogProps) {
  const [pairs, setPairs] = useState<CurrencyPair[]>([...DEFAULT_PAIRS])

  useEffect(() => {
    if (open) {
      setPairs(getStoredCurrencyPairs())
    }
  }, [open])

  const handleSave = () => {
    const valid = pairs.filter((p) => p.base && p.symbol && p.base !== p.symbol).slice(0, MAX_PAIRS)
    setStoredCurrencyPairs(valid.length ? valid : DEFAULT_PAIRS)
    onSave?.(valid.length ? valid : DEFAULT_PAIRS)
    onOpenChange(false)
  }

  const setPairAt = (index: number, field: "base" | "symbol", value: string) => {
    setPairs((prev) => {
      const next = prev.map((p) => ({ ...p }))
      while (next.length <= index) next.push({ base: "USD", symbol: "EUR" })
      next[index] = { ...next[index], [field]: value }
      return next.slice(0, MAX_PAIRS)
    })
  }

  const getPairAt = (index: number): CurrencyPair => pairs[index] ?? { base: "USD", symbol: "EUR" }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent
          hideCloseButton={false}
          className="left-[50%] top-[calc(3.5rem+12px)] translate-x-[-50%] translate-y-0 max-w-[min(90vw,420px)] p-0 gap-0"
        >
          <DialogTitle className="sr-only">Currency Selection</DialogTitle>
          <DialogDescription className="sr-only">
            Select up to 4 currency pairs to monitor exchange rates.
          </DialogDescription>
          <div className="w-[min(90vw,420px)] rounded-lg border bg-popover text-popover-foreground shadow-md overflow-hidden">
            <div className="p-3 border-b bg-muted/30">
              <h3 className="font-medium text-sm">Exchange rates to monitor</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Choose up to 4 pairs; each can have a different base currency.
              </p>
            </div>
            <div className="p-3 space-y-4">
              <Label className="text-xs">Currency pairs (up to 4)</Label>
              <div className="space-y-3">
                {[0, 1, 2, 3].map((i) => {
                  const { base, symbol } = getPairAt(i)
                  return (
                    <div key={i} className="grid grid-cols-2 gap-2 items-center">
                      <div className="space-y-1">
                        <span className="text-[10px] text-muted-foreground">Base</span>
                        <Select value={base} onValueChange={(v) => setPairAt(i, "base", v)}>
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {COMMON_CURRENCIES.filter((c) => c !== symbol).map((c) => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                            {symbol && (
                              <SelectItem value={symbol}>{symbol}</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <span className="text-[10px] text-muted-foreground">Target</span>
                        <Select value={symbol} onValueChange={(v) => setPairAt(i, "symbol", v)}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {COMMON_CURRENCIES.filter((c) => c !== base).map((c) => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                            {base && (
                              <SelectItem value={base}>{base}</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
            <div className="p-3 border-t flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave}>
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}
